import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AdminPageHeader, Field, Select, Toggle, ConfirmDeleteButton } from "./_helpers";
import MediaPicker from "@/components/admin/MediaPicker";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

const empty = {
  title: "",
  description: "",
  image: "",
  options: [
    { text: "", image: "" },
    { text: "", image: "" },
  ],
  province_id: "",
  is_active: true,
  show_on_homepage: false,
  show_results: true,
};

export default function AdminPolls() {
  const [items, setItems] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/polls").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/provinces?active_only=false").then((r) => setProvinces(r.data));
  }, []);

  const startEdit = (p) => {
    setEditing(p.id);
    setF({
      title: p.title || "",
      description: p.description || "",
      image: p.image || "",
      options: (p.options || []).map((o) => ({ text: o.text || "", image: o.image || "" })),
      province_id: p.province_id || "",
      is_active: p.is_active !== false,
      show_on_homepage: !!p.show_on_homepage,
      show_results: p.show_results !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditing(null); setF(empty); };

  const save = async () => {
    try {
      const payload = {
        ...f,
        province_id: f.province_id || null,
        options: f.options.filter((o) => (o.text || "").trim()),
      };
      if (editing) await api.put(`/polls/${editing}`, payload);
      else await api.post("/polls", payload);
      setF(empty);
      setEditing(null);
      load();
      toast.success(editing ? "Anket güncellendi" : "Anket eklendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  return (
    <div>
      <AdminPageHeader title="Anketler" />
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-100 rounded-lg p-4 space-y-3">
          {items.map((p) => (
            <div key={p.id} className={`border border-gray-100 rounded p-3 ${editing === p.id ? "bg-amber-50" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {p.image && <img src={p.image} alt="" className="w-10 h-10 rounded object-cover" />}
                  <div>
                    <div className="font-bold">{p.title}</div>
                    <div className="text-[10px] text-gray-500 flex gap-2">
                      {p.is_active ? <span className="text-emerald-600">AÇIK</span> : <span>KAPALI</span>}
                      {p.show_on_homepage && <span className="text-[#e11d2a] font-bold">ANASAYFA</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={async () => { await api.put(`/polls/${p.id}`, { ...p, options: p.options.map(o => ({text: o.text, image: o.image})), show_on_homepage: !p.show_on_homepage }); load(); }}
                    className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                    data-testid={`poll-toggle-home-${p.id}`}
                  >
                    {p.show_on_homepage ? "Anasayfadan Kaldır" : "Anasayfada Göster"}
                  </button>
                  <button
                    onClick={async () => { await api.put(`/polls/${p.id}`, { ...p, options: p.options.map(o => ({text: o.text, image: o.image})), is_active: !p.is_active }); load(); }}
                    className="text-xs text-blue-600"
                  >
                    {p.is_active ? "Kapat" : "Aç"}
                  </button>
                  <button onClick={() => startEdit(p)} className="text-xs text-blue-600">Düzenle</button>
                  <ConfirmDeleteButton onConfirm={async () => { await api.delete(`/polls/${p.id}`); load(); }} />
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">{p.total_votes || 0} oy</div>
              <div className="mt-2 space-y-1">
                {(p.options || []).map((o) => (
                  <div key={o.id} className="text-xs flex justify-between items-center gap-2">
                    <span className="flex items-center gap-1.5 truncate">
                      {o.image && <img src={o.image} alt="" className="w-6 h-6 rounded object-cover" />}
                      <span className="truncate">{o.text}</span>
                    </span>
                    <span className="shrink-0">{o.votes}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="text-center text-sm text-gray-400 py-8">Henüz anket yok</div>
          )}
        </div>
        <div className="bg-white border border-gray-100 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">{editing ? "Anketi Düzenle" : "Yeni Anket"}</h3>
            {editing && (
              <button onClick={cancelEdit} className="text-gray-500 hover:text-gray-700"><X className="w-4 h-4" /></button>
            )}
          </div>
          <Field label="Başlık" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <Field label="Açıklama" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Soru Görseli (opsiyonel)</label>
            <MediaPicker
              value={f.image}
              onChange={(u) => setF({ ...f, image: u })}
              scope="polls"
              testId="poll-image-picker"
            />
          </div>
          <Select
            label="İl"
            value={f.province_id || ""}
            options={[{ value: "", label: "Genel" }, ...provinces.map((p) => ({ value: p.id, label: p.name }))]}
            onChange={(e) => setF({ ...f, province_id: e.target.value })}
          />
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Seçenekler</label>
            {f.options.map((o, i) => (
              <div key={i} className="border rounded p-2 mb-2 space-y-2 bg-gray-50">
                <div className="flex gap-2 items-center">
                  <input
                    value={o.text}
                    onChange={(e) => { const opts = [...f.options]; opts[i] = { ...opts[i], text: e.target.value }; setF({ ...f, options: opts }); }}
                    placeholder={`Seçenek ${i + 1}`}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm"
                    data-testid={`poll-option-text-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => setF({ ...f, options: f.options.filter((_, j) => j !== i) })}
                    className="text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <MediaPicker
                  value={o.image}
                  onChange={(u) => { const opts = [...f.options]; opts[i] = { ...opts[i], image: u }; setF({ ...f, options: opts }); }}
                  scope="polls"
                  testId={`poll-option-image-${i}`}
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() => setF({ ...f, options: [...f.options, { text: "", image: "" }] })}
              className="text-xs text-blue-600 inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Seçenek ekle
            </button>
          </div>
          <Toggle
            label="Anasayfa bloğunda göster"
            value={f.show_on_homepage}
            onChange={(v) => setF({ ...f, show_on_homepage: v })}
            data-testid="poll-homepage-toggle"
          />
          <Toggle label="Aktif" value={f.is_active} onChange={(v) => setF({ ...f, is_active: v })} />
          <button
            onClick={save}
            className="w-full px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold"
            data-testid="poll-save-btn"
          >
            {editing ? "Güncelle" : "Oluştur"}
          </button>
        </div>
      </div>
    </div>
  );
}
